import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const fmtDate = (ms: number) => {
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export function Archive() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(0);

  // Debounce so we don't fire a scan on every keystroke; reset to page 0 on a new query.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); setPage(0); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const query = useQuery({ queryKey: ["archive", debouncedQ, page], queryFn: () => api.archive({ q: debouncedQ, page }) });
  const data = query.data;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Archive</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Imported HubSpot tickets, kept for record-keeping. Read-only, and excluded from the live queues and SLA.
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #e2e0d8", borderRadius: 8, padding: "0 10px" }}>
        <Icon name="search" size={15} color="#8a8e88" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by subject, requester email, brand, or HubSpot ticket #…"
          style={{ flex: 1, border: 0, outline: "none", fontSize: 13, padding: "9px 0", background: "transparent" }}
        />
        {q && <button onClick={() => setQ("")} style={{ border: 0, background: "none", cursor: "pointer", color: "#8a8e88", fontSize: 12 }}>Clear</button>}
      </div>

      {query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorNote error={query.error} />
      ) : data ? (
        <>
          <div style={{ fontSize: 12, color: "#8a8e88" }}>
            {data.total.toLocaleString()} ticket{data.total === 1 ? "" : "s"}{debouncedQ ? ` matching “${debouncedQ}”` : ""}
            {query.isFetching ? " · searching…" : ""}
          </div>

          <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" }}>
            {data.items.length === 0 ? (
              <div style={{ padding: 24, color: "#8a8e88", fontSize: 13 }}>No archived tickets{debouncedQ ? " match your search" : ""}.</div>
            ) : (
              data.items.map((t) => (
                <div
                  key={t.id}
                  onClick={() => navigate({ to: "/tickets/$ticketId", params: { ticketId: t.id } })}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderTop: "1px solid #f4f3ee", cursor: "pointer" }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                      <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5, color: "#006747", flex: "none" }}>{t.ref}</span>
                      <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject || "(no subject)"}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: "#8a8e88", display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span>{t.requester}</span>
                      <span>· {t.clientName}</span>
                      <span>· {fmtDate(t.receivedAt)}</span>
                      {t.hubspotId ? <span style={{ fontFamily: "'Space Mono',monospace", opacity: 0.8 }}>· HS #{t.hubspotId}</span> : null}
                    </div>
                  </div>
                  <Icon name="chevron-right" size={16} color="#c9ccc6" />
                </div>
              ))
            )}
          </div>

          {data.pages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, fontSize: 12.5, color: "#4a504a" }}>
              <button
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                style={{ border: "1px solid #e2e0d8", background: "#fff", borderRadius: 7, padding: "6px 12px", cursor: page <= 0 ? "default" : "pointer", opacity: page <= 0 ? 0.5 : 1 }}
              >
                Previous
              </button>
              <span>Page {page + 1} of {data.pages.toLocaleString()}</span>
              <button
                disabled={page + 1 >= data.pages}
                onClick={() => setPage((p) => p + 1)}
                style={{ border: "1px solid #e2e0d8", background: "#fff", borderRadius: 7, padding: "6px 12px", cursor: page + 1 >= data.pages ? "default" : "pointer", opacity: page + 1 >= data.pages ? 0.5 : 1 }}
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : null}
    </Page>
  );
}
